/**
 * `R2-UI2` — Formulario «Nuevo envío» de la pantalla «Envíos» (`F2`).
 *
 * ⭐ RASTRO · 23/9/2026 · `R2-UI2`. Las tres cosas que cambian respecto de `AW10`,
 * y el motivo de cada una:
 *
 *  ① **El selector de plantilla lee la LISTA «Plantillas para envíos»**, no el
 *     catálogo. `B13`/`RT 9` (`PRD_R2.md:845`): lo que se OFRECE en Nuevo envío es
 *     lo que el brazo puso en la lista, y si se puede enviar lo dice
 *     `FilaParaEnvios.sendability`, que llega DECIDIDO desde el borde. Derivarlo
 *     del `approvalStatus` dejaría dos lugares decidiendo.
 *  ② **El bloque «Variables» dejó de ser una maqueta**: una fila por variable real
 *     de la plantilla (`PlantillaDelBrazo.variables`, en ORDEN), cada una eligiendo
 *     el dato del participante (`TemplateParameterSource`, vocabulario CERRADO de
 *     `ADR-0058`). El campo «Parámetro» de `AW10` desaparece: era el placeholder de
 *     esto.
 *  ③ **La programación es texto `dd-mm-aaaa hh:mm`, hora Chile.** `datetime-local`
 *     queda fuera: su valor es un instante del reloj del navegador y el envío sale
 *     por un brazo con hora fija.
 *
 * `guard` ⑦ · PII: este archivo solo RECIBE filas de `cargarElegibles` y las
 * MUESTRA. No contiene nombres, teléfonos ni datos de persona, ni como literales,
 * ni como ejemplos, ni como comentarios. Los tipos describen el formato
 * (`nombre`, `telefono`) en palabras, igual que `alta.tsx` hace con
 * `ParticipanteEnSesion`.
 */

import { useEffect, useMemo, useState } from "react"
import type { JSX } from "react"
import { useNavigate } from "react-router"
import { useBordeEnvios } from "../contexto-borde-envios.tsx"
/*
 * ⛔ `R2-UI2` · el tipo del comando sale de `./borde-envios.ts` (`ComandoDeEnvio`).
 * Importar `CreateDispatchCommand` acá otra vez era la fuente de los dos lugares
 * decidiendo. ⭐ RASTRO · 24/9/2026, `R2-UI15`: ese tipo ~~agregaba `exclude` al
 * `Omit<CreateDispatchCommand, "arm">`~~; ahora sale de `CreateDispatchRequestBody`
 * del contrato (`audience` O `recipients`), y un `exclude` suelto no compila.
 */
import type { Estado } from "@contracts/participants/index.ts"
import type { TemplateContent, TemplateParameterSource } from "@contracts/channel/index.ts"
import { Paginado } from "../paginado.tsx"
// ⭐ `CA-18` · el rótulo del lote se REUSA del padrón en vez de copiarse: el mismo
// valor vacío es «Alta manual» en la tabla del padrón y tiene que leerse igual acá.
import { rotuloDeLote } from "../padron/formato.ts"
// ⭐ `R2-UI13` / `CA-27` · los flags de la Tabla 1 se rotulan con la MISMA función que
// usa Participantes («Sí»/«No»), importada y no copiada.
import { rotuloDeBandera } from "../padron/formato.ts"
import type {
  CargarPlantillas,
  FilaParaEnvios,
  ListaParaEnvios,
} from "../plantillas/borde-plantillas.ts"
import type { ComandoDeEnvio } from "./borde-envios.ts"
import { selloDelBrazo } from "../zonas.ts"
/*
 * ⭐ `R2-UI13` / `CA-19`/`CA-20`/`CA-24` · «Pidió humano» —la opción del filtro y la
 * columna de la Tabla 1— se decide con la MISMA función que Participantes y
 * Métricas (`src/padron/tabla.tsx`). Nunca por el nombre del brazo: el nombre es
 * etiqueta y «no puede alterar comportamiento» (`zonas.ts`, §`nombreDelBrazo`).
 */
import { derivaAHumano } from "../zonas.ts"
import type { Brazo } from "../zonas.ts"

/**
 * Una fila de la Tabla 1 de «Nuevo envío».
 *
 * ⭐ RASTRO · 24/9/2026 · `R2-UI13` (`PRD_R2.md:1402`, `CA-23`/`CA-27`). Eran tres
 * campos (`idUsuario`, `nombre`, `telefono`) y la consola TIRABA los otros cuatro
 * que `EligibleListItem` ya trae por el cable. Ahora son los siete: el lote y los
 * tres estados salen de la MISMA lectura de elegibles, sin cruzar contra el padrón
 * (que traería `optOut` y las `…FechaHora`, que esta vista deja fuera a propósito).
 */
export type FilaDeAudiencia = {
  readonly idUsuario: string
  readonly nombre: string
  readonly telefono: string
  readonly lote: string
  readonly sinInteraccion: boolean
  readonly siguioConChatbot: boolean
  readonly pidioHumano: boolean
}

/**
 * La consulta de elegibles.
 *
 * ⭐ `R2-UI2` · **`lotes` y `estados` entraron acá** y viajan como parámetros
 * REPETIDOS (`?lotes=A&lotes=B`), que es como el servidor los lee como OR. Ver
 * `borde-elegibles.ts`. Un filtro aplicado en el servidor es lo único que hace
 * que la selección total signifique «todos los elegibles del filtro» y no «todos
 * los de esta página».
 */
export type ConsultaDeAudiencia = {
  readonly pagina: number
  readonly tamanoDePagina: number
  readonly lotes: readonly string[]
  readonly estados: readonly Estado[]
}

export type PaginaDeAudiencia = {
  readonly filas: readonly FilaDeAudiencia[]
  readonly total: number
}

export type CargarElegibles = (consulta: ConsultaDeAudiencia) => Promise<PaginaDeAudiencia>

/**
 * La fuente de la lista «Plantillas para envíos» del brazo, ya cruzada contra el
 * catálogo (`BordePlantillas.listaParaEnvios`). El brazo lo resolvió quien monta
 * la ruta; acá no se lee.
 */
export type CargarListaParaEnvios = () => Promise<ListaParaEnvios>

/**
 * El estado de la selección, que es lo que `B26`/`RT 6` piden poder expresar:
 * **«todos los elegibles del filtro, menos los que destildé»**, sin bajar las
 * ~16.000 filas al navegador (`ADR-0090`).
 *
 * · `todos: false` → se manda `ids`.
 * · `todos: true`  → se manda «todo lo visto» menos `excluidos`.
 */
export type SeleccionDeAudiencia = {
  readonly todos: boolean
  readonly ids: ReadonlySet<string>
  readonly excluidos: ReadonlySet<string>
}

export type FiltrosDeAudiencia = {
  readonly lotes: readonly string[]
  readonly estados: readonly Estado[]
}

const TAMANOS_DE_PAGINA = [50, 100, 200, 500, 1000, 2000, 3000] as const

/**
 * Los estados que se pueden filtrar. Los valores son del contrato
 * (`contracts/participants`: `Estado`) y `tsc` sostiene el literal contra el tipo,
 * así que agregar un cuarto estado sin agregarlo acá NO compila.
 *
 * ⭐ RASTRO · 24/9/2026 · `R2-UI13` (`PRD_R2.md:1399`: «Pidió humano» sólo en el
 * brazo con derivación; `:1472`; `CA-19`/`CA-20`). Esta lista se dibujaba ENTERA en los
 * dos brazos. Sigue siendo la lista completa del contrato, pero lo que se OFRECE
 * lo decide `estadosDelBrazo`: «Pidió humano» sólo donde el brazo deriva.
 */
const ESTADOS_DEL_BRAZO: readonly Estado[] = [
  "sin_interaccion",
  "siguio_con_chatbot",
  "pidio_humano",
]

/**
 * ⭐ `R2-UI13` · los estados que el filtro OFRECE en este brazo. `deriva` sale de
 * `derivaAHumano(brazo)` (una sola vez por render, la misma respuesta que usa la
 * columna de la tabla): un brazo sin derivación no tiene quien «pida humano».
 */
function estadosDelBrazo(deriva: boolean): readonly Estado[] {
  return deriva
    ? ESTADOS_DEL_BRAZO
    : ESTADOS_DEL_BRAZO.filter((estado) => estado !== "pidio_humano")
}

const ROTULO_DE_ESTADO: Record<Estado, string> = {
  sin_interaccion: "Sin interacción",
  siguio_con_chatbot: "Siguió con el chatbot",
  pidio_humano: "Pidió humano",
}

/**
 * ⭐⭐ `ADR-0176` §① y §ENMIENDA — **el rótulo de audiencia se escribe en UN SOLO
 * LUGAR, y de acá salen la PANTALLA y el REGISTRO.** El denominador es
 * `PaginaDeAudiencia.total` (el total en la base del conjunto consultado), nunca
 * `filas.length` ni `recipients.length`.
 */
/**
 * ⭐ `R2-UI15` · `DispatchAudienceFilter` pide una tupla NO vacía o `null`: «sin
 * filtrar por esto» se escribe `null`, nunca `[]` (que el servidor rechaza).
 */
function tuplaONull<T>(lista: readonly T[]): readonly [T, ...T[]] | null {
  const [primero, ...resto] = lista
  return primero === undefined ? null : [primero, ...resto]
}

function etiquetaDeAudiencia(seleccionados: number, elegibles: number): string {
  return `${seleccionados} seleccionados de ${elegibles} elegibles`
}

/**
 * ⭐⭐ `R2-UI16` (R2-H-044, B29, errata `R2-D3`) · **la SEGUNDA línea de
 * Audiencia**, la que el envío guarda como `audienceFilterLabel`. La arma la
 * consola —con `recipients` (CA-48) al servidor sólo le llegan ids— a partir del
 * filtro APLICADO (el que dio M), nunca del borrador de casillas.
 *
 * Texto de `PRD_R2.md:1456`: «Lotes: [lotes separados por coma] · Estados:
 * [estados separados por coma]»; si no se marcó lote no aparece la parte
 * «Lotes», si no se marcó estado no aparece «Estados», y sin ninguno no hay
 * segunda línea ⇒ `null` (nunca `""`, guard 4). Cada lote con el rótulo de su
 * casilla (`rotuloDeLote`); cada estado con su nombre del PRD (`:1399`).
 *
 * ⚠️ SUPUESTO de la etapa (el PRD no lo fija): los lotes van en el orden de sus
 * casillas (`ordenDeLotes`, lo que devolvió `GET elegibles/lotes`) y los estados
 * en el del PRD; un lote aplicado que no esté en `ordenDeLotes` va al final, en
 * el orden en que se aplicó.
 *
 * ⛔⭐ **RASTRO · 24/9/2026, `R2-UI16`**. El formulario mostraba debajo del filtro
 * una nota ~~«Lotes: {…|todos} · Estados: {…|todos}»~~ con las dos partes
 * siempre y «todos» en la vacía: no era del PRD y contradecía `:1456`. R2-D3 la
 * QUITA; esta función tiene un solo uso, el cuerpo del `POST`.
 */
export function segundaLineaDeAudiencia(
  aplicado: FiltrosDeAudiencia,
  ordenDeLotes: readonly string[] = [],
): string | null {
  const lotes = [
    ...ordenDeLotes.filter((lote) => aplicado.lotes.includes(lote)),
    ...aplicado.lotes.filter((lote) => !ordenDeLotes.includes(lote)),
  ]
  const estados = ESTADOS_DEL_BRAZO.filter((estado) => aplicado.estados.includes(estado))
  const partes: string[] = []
  if (lotes.length > 0) partes.push(`Lotes: ${lotes.map(rotuloDeLote).join(", ")}`)
  if (estados.length > 0) {
    partes.push(`Estados: ${estados.map((estado) => ROTULO_DE_ESTADO[estado]).join(", ")}`)
  }
  return partes.length === 0 ? null : partes.join(" · ")
}

/**
 * `CP-F2A-003` §② · los rótulos de los campos obligatorios, en un solo lugar: se
 * usan en el `<label>`, en el mensaje de error y en el resumen de la live region,
 * y tienen que coincidir.
 */
const ROTULO_NOMBRE = "Nombre del envío"
const ROTULO_PLANTILLA = "Plantilla de WhatsApp"
/*
 * ⭐ RASTRO · 24/9/2026 · `R2-UI13` (`PRD_R2.md:1398-1399` «Audiencia · Lotes»,
 * «Audiencia · Estados»; `CA-05`). Decía ~~«Selección de usuarios»~~, el rótulo de
 * R1.1. Al ser UNA constante, cambia a la vez en la `<legend>` y en la región de
 * errores de arriba.
 */
const ROTULO_AUDIENCIA = "Audiencia"
const ROTULO_PROGRAMACION = "Programación"
const ROTULO_VARIABLES = "Variables"

const MARCA_OBLIGATORIO = "(obligatorio)"

const ID_ERROR_NOMBRE = "envio-nombre-error"
const ID_ERROR_PLANTILLA = "envio-plantilla-error"
const ID_ERROR_AUDIENCIA = "envio-audiencia-error"
const ID_ERROR_PROGRAMACION = "envio-programacion-error"

/**
 * `R1.1-F5-CA-85` · **tú, sin voseo** (`PRD_R1.1.md:798`, `:904`). El texto de la
 * opción vacía deja de ser «Elegí…» y el mensaje de audiencia dice «marca».
 */
/**
 * ⭐ `R2-UI2` · la opción inicial nombra lo que el operador elige: la LISTA del
 * brazo (`plantillas-para-envios`), no el catálogo de aprobadas. El texto viejo
 * («— Elige una plantilla aprobada —») era el del selector de `AW46`.
 */
const OPCION_INICIAL_DE_LA_LISTA = "— Elige una plantilla —"

/**
 * ⭐ `R2-UI2`/`CA-39…45` · **los errores del envío, uno por campo.** `Regla 10`
 * prohíbe esconder el problema en un botón gris: al apretar «Enviar» se dicen
 * TODOS los que faltan, juntos. Los literales son los del `acceptance`
 * (`gates/r1/R2-UI2.gate.ts`, `envio-habilitado`).
 */
const MENSAJE_FALTA_NOMBRE = "Escribe el nombre del envío."
const MENSAJE_NOMBRE_LARGO = "El nombre tiene como máximo 100 caracteres."
const MENSAJE_FALTA_PLANTILLA = "Elige una plantilla."
const MENSAJE_FALTA_VARIABLE = "Elige con qué se completa cada variable."
const MENSAJE_FALTA_AUDIENCIA = "Selecciona al menos un participante."
const MENSAJE_PROGRAMACION_INVALIDA = "Escribe la fecha y hora como dd-mm-aaaa hh:mm."
const MENSAJE_PROGRAMACION_PASADA = "La programación tiene que ser una fecha y hora futura."

/** `CA-76` · el tope del nombre, en un solo lugar. */
const LIMITE_DE_NOMBRE = 100

/**
 * ⭐ `R2-UI12` · **las ayudas de cada campo, LITERALES del PRD y cada una en UNA
 * constante** (`PRD_R2.md:1396-1399`, `:1562`; `CA-07`, `CA-16`, `CA-21`,
 * `CA-22`, `CA-17`). Los casos comparan el texto: una paráfrasis «que dice lo
 * mismo» es un rojo — ya pasó en este archivo con la de Programación (ver el
 * rastro junto a su `<p>`).
 */
const AYUDA_PLANTILLA = "Solo las plantillas de «Plantillas para envíos»."
const AYUDA_VARIABLES = "Cada variable se completa con un dato del participante que recibe el mensaje."
const AYUDA_LOTES = "Sin lotes marcados = todos los lotes del brazo."
const AYUDA_ESTADOS =
  "Entra quien tenga cualquiera de los estados marcados. Sin estados marcados = todos."
const AYUDA_PROGRAMACION = "Vacía = sale en el momento. Hora de Chile."

/**
 * ⭐ `R2-UI12` / `CA-25` (`PRD_R2.md:1402`, `:1570`) · la leyenda de la tabla de
 * elegibles. El nombre del brazo sale de `selloDelBrazo` (la declaración de
 * zonas: instancia + brazo), NUNCA de un literal. Sin brazo en la ruta no se
 * inventa uno: la leyenda dice lo que sabe.
 */
/**
 * ⭐ `R2-UI13` / `CA-04` (`PRD_R2.md:1394`) · el aviso de por qué número sale el
 * envío y a quién nunca, ENTERO y en un solo lugar. El nombre del brazo es el
 * sello de la instancia CONFIGURADA (`selloDelBrazo`, errata `R2-E3`), nunca un
 * literal.
 *
 * ⭐ RASTRO · 24/9/2026 · `R2-UI13`. Terminaba en ~~«…y solo a sus
 * participantes.»~~: le faltaba «, nunca a quienes tienen opt-out.».
 */
function avisoDelEnvio(brazo: Brazo): string {
  return `El envío sale por el número de WhatsApp de ${selloDelBrazo(brazo)} y solo a sus participantes, nunca a quienes tienen opt-out.`
}

/**
 * ⭐ `R2-UI13` / errata `R2-D2` (`PRD_R2.md:1402`: «se muestra acortado a sus
 * primeros caracteres seguidos de «…»»). ⚠️ El PRD NO fija cuántos: 8 es un
 * SUPUESTO de la etapa (el primer bloque de un UUID), sin decisión del dueño del
 * PRD. Si cambia, cambia acá y en ningún otro lado.
 */
const LARGO_DEL_ID_VISIBLE = 8

/**
 * Lo que se VE en la celda «ID de usuario»: el prefijo y un «…» (U+2026, uno).
 *
 * ⛔ Es SÓLO presentación. La `key` de la fila, la marca (`estaMarcada` /
 * `alternarFila`), el `aria-label` de la casilla y todo lo que viaja al servidor
 * siguen usando el id ENTERO: acortarlo en la fila haría que dos participantes
 * cuyo id empieza igual compartieran marca, y que «excluidos» mandara prefijos
 * que el servidor no reconoce. Un id que ya es tan corto como el prefijo se
 * muestra entero y sin «…»: acortar algo que no se acorta mentiría.
 */
function idVisible(idUsuario: string): string {
  return idUsuario.length <= LARGO_DEL_ID_VISIBLE
    ? idUsuario
    : `${idUsuario.slice(0, LARGO_DEL_ID_VISIBLE)}…`
}

function leyendaDeElegibles(brazo: Brazo | undefined): string {
  return brazo === undefined
    ? "Participantes elegibles (sin opt-out)."
    : `Participantes elegibles de ${selloDelBrazo(brazo)} (sin opt-out).`
}

/**
 * ⭐ `R2-UI12` / `CA-15`/`CA-41` · el `<select>` de cada variable arranca SIN
 * ELEGIR, y ésta es la opción que lo dice. Su valor `""` no es un
 * `TemplateParameterSource`: nunca viaja (la guarda de `enviar()` corta antes).
 */
const OPCION_VARIABLE_SIN_ELEGIR = "— Elige un dato —"

const ID_ERROR_VARIABLES = "envio-variables-error"

/**
 * ⭐ El formato visible de la programación. `scheduledForLocal` del contrato es
 * exactamente esto (`contracts/dispatch-api/index.ts`), hora Chile.
 */
const FORMATO_PROGRAMACION = "dd-mm-aaaa hh:mm"
const PATRON_PROGRAMACION = /^(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2})$/

/*
 * ⭐ SELECTOR DE FECHA Y HORA (29/10/2026). Los dos controles nativos NO hablan el
 * formato del contrato, así que cada uno tiene su patrón: `<input type="date">`
 * entrega `aaaa-mm-dd` y `<input type="time">` entrega `hh:mm` (algunos navegadores
 * agregan `:ss`). El pegamento está en `componerProgramacion`.
 */
const PATRON_DIA = /^(\d{4})-(\d{2})-(\d{2})$/
const PATRON_HORA = /^(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/

/**
 * Lo que se valida cuando el operador eligió UNA de las dos mitades y no la otra.
 * No es un texto para mostrar: cae en `parsearProgramacion` y sale como
 * `MENSAJE_PROGRAMACION_INVALIDA`, que es el error de su campo (`CA-78`).
 */
const PROGRAMACION_INCOMPLETA = "incompleta"

/**
 * ⭐ `CA-44`/`CA-78` · **los días del mes, contados, no adivinados.** Un `31-02`
 * NO da `Invalid Date` en V8: se desborda a marzo. Por eso la fecha se valida por
 * COMPONENTES contra este número antes de construir nada.
 */
function diasDelMes(anio: number, mes: number): number {
  return new Date(Date.UTC(anio, mes, 0)).getUTCDate()
}

/**
 * ⭐ SELECTOR DE FECHA Y HORA (29/10/2026) · **pega los dos controles nativos en el
 * texto del contrato**, sin pasar por `Date` ni por la zona del navegador:
 * `aaaa-mm-dd` + `hh:mm` ⇒ `dd-mm-aaaa hh:mm` (hora Chile, que es la que el
 * servidor interpreta).
 *
 * Las tres salidas importan:
 *  · `""` — no eligió nada: el envío sale en el momento (`scheduledFor: null`).
 *  · `PROGRAMACION_INCOMPLETA` — eligió UNA mitad: cae en el error de forma de su
 *    campo. Sin esto, «fecha sin hora» se leería como «al momento» y el envío
 *    saldría en el acto sin que nadie lo pidiera.
 *  · el texto — las dos mitades, listo para `parsearProgramacion`.
 */
function componerProgramacion(dia: string, hora: string): string {
  const fecha = PATRON_DIA.exec(dia.trim())
  const reloj = PATRON_HORA.exec(hora.trim())
  if (fecha === null && reloj === null) return ""
  if (fecha === null || reloj === null) return PROGRAMACION_INCOMPLETA
  const [, aaaa, mm, dd] = fecha
  const [, hh, mi] = reloj
  if (
    aaaa === undefined ||
    mm === undefined ||
    dd === undefined ||
    hh === undefined ||
    mi === undefined
  ) {
    return PROGRAMACION_INCOMPLETA
  }
  return `${dd}-${mm}-${aaaa} ${hh}:${mi}`
}

/**
 * Convierte el texto `dd-mm-aaaa hh:mm` (hora Chile) al instante que viaja en el
 * comando. `""` = «al momento» (`scheduledFor: null`).
 *
 * ⚠️ El offset `-04:00` es el de Chile en horario de invierno. El contrato fija la
 * HORA DEL BRAZO, no la del navegador: por eso no se usa `new Date(texto)` —que
 * interpretaría el texto en la zona del operador— ni el input nativo de fecha.
 */
function parsearProgramacion(texto: string): Date | null | "invalida" {
  const limpio = texto.trim()
  if (limpio === "") return null
  const partes = PATRON_PROGRAMACION.exec(limpio)
  if (partes === null) return "invalida"
  const [, dd, mm, aaaa, hh, mi] = partes
  if (
    dd === undefined ||
    mm === undefined ||
    aaaa === undefined ||
    hh === undefined ||
    mi === undefined
  ) {
    return "invalida"
  }
  const dia = Number(dd)
  const mes = Number(mm)
  const anio = Number(aaaa)
  const hora = Number(hh)
  const minuto = Number(mi)
  if (mes < 1 || mes > 12 || hora > 23 || minuto > 59) return "invalida"
  /*
   * ⛔ `CA-44` · 31-02 NO existe, y `new Date("2026-02-31T…")` no lo detecta:
   * desborda a marzo. La única forma de rechazarlo es comparar el día contra los
   * días REALES del mes.
   */
  if (dia < 1 || dia > diasDelMes(anio, mes)) return "invalida"
  const fecha = new Date(`${aaaa}-${mm}-${dd}T${hh}:${mi}:00-04:00`)
  return Number.isNaN(fecha.getTime()) ? "invalida" : fecha
}

type SeleccionProps = {
  readonly cargarElegibles: CargarElegibles
  /** `GET elegibles/lotes` del brazo. Ausente ⇒ no se ofrece filtro por lote. */
  readonly lotesDe?: () => Promise<readonly string[]>
  readonly seleccion: SeleccionDeAudiencia
  readonly onSeleccionChange: (seleccion: SeleccionDeAudiencia) => void
  readonly total: number
  readonly onTotalChange: (total: number) => void
  /**
   * `R2-UI16` · el segundo argumento es el ORDEN de las casillas de lote (lo que
   * devolvió `lotesDe`), para escribir la segunda línea en ese orden.
   */
  readonly onFiltrosChange: (filtros: FiltrosDeAudiencia, ordenDeLotes: readonly string[]) => void
  /** `CP-F2A-003` §② — si la audiencia está vacía, este grupo lo dice. */
  readonly falta: boolean
  /** `R2-UI12` / `CA-25` · el brazo de la ruta, para la leyenda de la tabla. */
  readonly brazo?: Brazo
}

function SeleccionDeAudiencia(props: SeleccionProps): JSX.Element {
  const {
    cargarElegibles,
    lotesDe,
    seleccion,
    onSeleccionChange,
    total,
    onTotalChange,
    onFiltrosChange,
    falta,
    brazo,
  } = props

  /**
   * ⭐ `R2-UI13` · ¿este brazo deriva a humano? UNA pregunta, UNA respuesta, para
   * la opción del filtro Y la columna de la tabla. Sin brazo en la ruta
   * (`null`), `derivaAHumano` muestra — lo mismo que Participantes.
   */
  const deriva = derivaAHumano(brazo ?? null)

  const [pagina, setPagina] = useState(1)
  const [tamanoDePagina, setTamanoDePagina] = useState<number>(50)
  const [filas, setFilas] = useState<readonly FilaDeAudiencia[]>([])
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** Los lotes del brazo, para el filtro. No se inventa una lista: se piden. */
  const [lotesDisponibles, setLotesDisponibles] = useState<readonly string[]>([])
  const [borradorLotes, setBorradorLotes] = useState<ReadonlySet<string>>(new Set())
  const [borradorEstados, setBorradorEstados] = useState<ReadonlySet<Estado>>(new Set())
  const [aplicados, setAplicados] = useState<FiltrosDeAudiencia>({ lotes: [], estados: [] })

  useEffect(() => {
    if (lotesDe === undefined) return
    let cancelado = false
    void lotesDe()
      .then((lotes) => {
        if (!cancelado) setLotesDisponibles(lotes)
      })
      .catch(() => {
        if (!cancelado) setLotesDisponibles([])
      })
    return () => {
      cancelado = true
    }
  }, [lotesDe])

  useEffect(() => {
    let cancelado = false

    async function cargar() {
      setCargando(true)
      setError(null)
      try {
        const resultado = await cargarElegibles({
          pagina,
          tamanoDePagina,
          lotes: aplicados.lotes,
          estados: aplicados.estados,
        })
        if (cancelado) return
        setFilas(resultado.filas)
        onTotalChange(resultado.total)
      } catch (causa) {
        if (cancelado) return
        setError(causa instanceof Error ? causa.message : String(causa))
      } finally {
        if (!cancelado) setCargando(false)
      }
    }

    void cargar()
    return () => {
      cancelado = true
    }
  }, [cargarElegibles, pagina, tamanoDePagina, aplicados, onTotalChange])

  const aplicarFiltros = () => {
    const siguiente: FiltrosDeAudiencia = {
      lotes: Array.from(borradorLotes),
      estados: Array.from(borradorEstados),
    }
    setAplicados(siguiente)
    onFiltrosChange(siguiente, lotesDisponibles)
    setPagina(1)
  }

  const limpiarFiltros = () => {
    setBorradorLotes(new Set())
    setBorradorEstados(new Set())
    const vacio: FiltrosDeAudiencia = { lotes: [], estados: [] }
    setAplicados(vacio)
    onFiltrosChange(vacio, lotesDisponibles)
    setPagina(1)
  }

  const alternarLote = (lote: string) => {
    const siguiente = new Set(borradorLotes)
    if (siguiente.has(lote)) siguiente.delete(lote)
    else siguiente.add(lote)
    setBorradorLotes(siguiente)
  }

  const alternarEstado = (estado: Estado) => {
    const siguiente = new Set(borradorEstados)
    if (siguiente.has(estado)) siguiente.delete(estado)
    else siguiente.add(estado)
    setBorradorEstados(siguiente)
  }

  const estaMarcada = (idUsuario: string): boolean =>
    seleccion.todos ? !seleccion.excluidos.has(idUsuario) : seleccion.ids.has(idUsuario)

  /**
   * ⭐ `R2-UI12` · **N y M de la barra, calculados UNA vez**: el rótulo y la
   * visibilidad de los dos botones leen los mismos dos números
   * (`PRD_R2.md:1401`). N = marcados (con selección total, M menos los
   * destildados); M = elegibles del filtro (`total` del servidor).
   */
  const marcados = seleccion.todos
    ? Math.max(0, total - seleccion.excluidos.size)
    : seleccion.ids.size

  const idsEnPagina = filas.map((fila) => fila.idUsuario)
  const todasEnPaginaMarcadas = filas.length > 0 && filas.every((fila) => estaMarcada(fila.idUsuario))

  /**
   * `CP-F2A-005` · **La casilla del encabezado NO PUEDE prometer «todos»**, y por
   * eso sigue diciendo «todos en esta página»: marca lo que está a la vista. La
   * selección TOTAL se expresa con el botón de abajo, que activa el criterio
   * (`SeleccionDeAudiencia.todos`) en vez de materializar las ~16.000 filas — la
   * forma que `ADR-0090` ya descartó.
   */
  const ETIQUETA_TODAS_EN_PAGINA = "todos en esta página"

  const alternarPagina = () => {
    const siguiente = new Set(seleccion.ids)
    if (todasEnPaginaMarcadas) {
      for (const id of idsEnPagina) siguiente.delete(id)
    } else {
      for (const id of idsEnPagina) siguiente.add(id)
    }
    onSeleccionChange({ ...seleccion, ids: siguiente })
  }

  const alternarFila = (idUsuario: string) => {
    if (seleccion.todos) {
      const excluidos = new Set(seleccion.excluidos)
      if (excluidos.has(idUsuario)) excluidos.delete(idUsuario)
      else excluidos.add(idUsuario)
      onSeleccionChange({ ...seleccion, excluidos })
      return
    }
    const ids = new Set(seleccion.ids)
    if (ids.has(idUsuario)) ids.delete(idUsuario)
    else ids.add(idUsuario)
    onSeleccionChange({ ...seleccion, ids })
  }

  return (
    <fieldset aria-describedby={falta ? ID_ERROR_AUDIENCIA : undefined}>
      <legend>
        {ROTULO_AUDIENCIA} <span aria-hidden="true">{MARCA_OBLIGATORIO}</span>
      </legend>

      {/*
        ⭐ `CA-28` · **la etiqueta de PANTALLA agrega «con este filtro»** cuando hay
        un filtro aplicado; la que se GUARDA (`audienceLabel`) NO — `RT 11`/`B29`:
        ese texto es de pantalla y el registro guarda la audiencia real, no el
        camino por el que se llegó a ella.
      */}
      <p aria-live="polite">
        {etiquetaDeAudiencia(marcados, total)}
        {aplicados.lotes.length > 0 || aplicados.estados.length > 0 ? " con este filtro" : ""}
      </p>

      {/*
        ⭐ `CP-F2A-003` §② · `descripcionDe` (la sonda de `AW10`) lee SÓLO el
        texto de este destino y TIRA si el `id` no existe, así que el `<p>` tiene
        que estar cuando (y sólo cuando) `falta` — ~~y su texto tiene que decir que
        el campo es OBLIGATORIO, no sólo el motivo~~. ⛔ Nada de `aria-hidden` acá:
        la razón para describir el campo es que un lector de pantalla la ESCUCHE.

        ⭐ RASTRO · 24/9/2026, noche · `R2-UI13` (errata `R2-D1` con su agregado,
        decisión de Galo; `CA-42`, `PRD_R2.md:1440`). Decía ~~«Selección de
        usuarios (obligatorio): Selecciona al menos un participante.»~~. R2-D1
        supera `CP-F2A-003` §② (R1.1): debajo del campo va SÓLO el texto del PRD,
        y es también la descripción accesible del grupo. Lo obligatorio lo sigue
        diciendo la marca de la `<legend>`; el rótulo, la región de arriba
        («Audiencia: …»).
      */}
      {falta && <p id={ID_ERROR_AUDIENCIA}>{MENSAJE_FALTA_AUDIENCIA}</p>}

      {/*
        ⛔ `R1.1-F5 · ninguna clase del JSX se queda sin regla en la hoja`: este
        contenedor NO lleva clase. `envios__filtros` existía acá y la hoja no tenía
        NI UNA regla para ella —una clase sin regla es un `<div>` que no hace nada,
        y el examen lo pone rojo—. La hoja (`estilos.css`) está FUERA del territorio
        de esta etapa, así que la reparación honesta es no estrenar la clase: los
        dos `<fieldset>` (lote/estado) conservan su agrupación por sí solos.
      */}
      <div>
        {lotesDisponibles.length > 0 && (
          <fieldset>
            <legend>Filtrar por lote</legend>
            {/* ⭐ `R2-UI12` / `CA-21` · la ayuda es del campo Lotes y va con él. */}
            <p className="nota">{AYUDA_LOTES}</p>
            {lotesDisponibles.map((lote) => (
              <label key={lote}>
                <input
                  type="checkbox"
                  checked={borradorLotes.has(lote)}
                  onChange={() => alternarLote(lote)}
                />
                {rotuloDeLote(lote)}
              </label>
            ))}
          </fieldset>
        )}

        <fieldset>
          <legend>Filtrar por estado</legend>
          {/* ⭐ `R2-UI12` / `CA-22` · cómo se combinan los estados. */}
          <p className="nota">{AYUDA_ESTADOS}</p>
          {estadosDelBrazo(deriva).map((estado) => (
            <label key={estado}>
              <input
                type="checkbox"
                checked={borradorEstados.has(estado)}
                onChange={() => alternarEstado(estado)}
              />
              {ROTULO_DE_ESTADO[estado]}
            </label>
          ))}
        </fieldset>

        <button type="button" onClick={aplicarFiltros}>
          Aplicar filtro
        </button>
        <button type="button" onClick={limpiarFiltros}>
          Limpiar
        </button>
      </div>

      {/*
        ⛔⭐ **RASTRO · 24/9/2026, `R2-UI12`** (`PRD_R2.md:1401`; `CA-33…36`, `CA-79`).
        Acá decía ~~«Seleccionar los {total} elegibles del filtro», visible
        SIEMPRE, y «Quitar selección total», visible sólo con `seleccion.todos`~~.
        Las dos cosas contradecían el PRD: «Seleccionar los M» se ve mientras
        falte alguno por marcar (N < M) y «Quitar selección» con al menos uno
        marcado (N > 0), así que con una selección PARCIAL se ven los dos — y con
        la condición vieja, marcar filas de a una escondía «Quitar selección»
        justo en el caso de `CA-79`. La visibilidad se calcula con N y M, no con
        `seleccion.todos`; el MODELO `{todos, ids, excluidos}` (B26) no cambia.
      */}
      <p>
        {marcados < total && (
          <button
            type="button"
            onClick={() =>
              onSeleccionChange({ todos: true, ids: seleccion.ids, excluidos: new Set() })
            }
          >
            {`Seleccionar los ${total}`}
          </button>
        )}
        {marcados > 0 && (
          <button
            type="button"
            onClick={() => onSeleccionChange({ todos: false, ids: new Set(), excluidos: new Set() })}
          >
            Quitar selección
          </button>
        )}
      </p>

      {cargando && <p>Cargando…</p>}
      {error !== null && <p role="alert">{error}</p>}

      {/*
        ⭐ `R2-UI12` / `CA-25` · la leyenda de la tabla. Va como párrafo ARRIBA y no
        como `<caption>`, por el mismo motivo que la leyenda de Envíos
        (`pantalla.tsx`, `R1.1-F4-CA-66`): un texto que nombra la zona no puede
        depender de que la tabla tenga filas. Reusa `.envios__leyenda`, que ya
        tiene regla en la hoja (la hoja está fuera de este territorio).
      */}
      <p className="envios__leyenda">{leyendaDeElegibles(brazo)}</p>

      <div className="tabla-rodante">
        <table className="envios__tabla-audiencia">
          <thead>
            <tr>
              <th>
                <label>
                  <input
                    type="checkbox"
                    checked={todasEnPaginaMarcadas}
                    onChange={alternarPagina}
                    aria-label={ETIQUETA_TODAS_EN_PAGINA}
                  />
                  {ETIQUETA_TODAS_EN_PAGINA}
                </label>
              </th>
              <th>ID de usuario</th>
              <th>Nombre</th>
              <th>Teléfono</th>
              {/*
                ⭐ `R2-UI13` / `CA-23`/`CA-24`/`CA-27` (`PRD_R2.md:1402`, `:1473`).
                Hasta acá la tabla terminaba en «Teléfono». «Pidió humano» sólo
                donde el brazo deriva (`deriva`, la misma respuesta que el filtro).
              */}
              <th>Lote</th>
              <th>{ROTULO_DE_ESTADO.sin_interaccion}</th>
              <th>{ROTULO_DE_ESTADO.siguio_con_chatbot}</th>
              {deriva && <th>{ROTULO_DE_ESTADO.pidio_humano}</th>}
            </tr>
          </thead>
          <tbody>
            {/*
              ⛔ `R2-UI13` / errata `R2-E1` (con su rastro del 24/9 noche) · las
              filas se dibujan en el orden EXACTO en que las manda el servidor: ni
              `sort`, ni agrupar por lote o estado. Ordenar acá ordenaría CADA
              PÁGINA por separado y disfrazaría el defecto de `CA-34`. El orden por
              nombre lo pone el servidor (`R2-PA2`).
            */}
            {filas.map((fila) => (
              <tr key={fila.idUsuario}>
                <td>
                  <input
                    type="checkbox"
                    checked={estaMarcada(fila.idUsuario)}
                    onChange={() => alternarFila(fila.idUsuario)}
                    aria-label={`Seleccionar fila de usuario ${fila.idUsuario}`}
                  />
                </td>
                {/*
                  ⭐ RASTRO · 24/9/2026, noche · `R2-UI13` (errata `R2-D2`). Era
                  ~~`{fila.idUsuario}`~~, el id entero. Ahora se ACORTA sólo lo
                  que se ve (`idVisible`); la `key`, la marca y el `aria-label`
                  de arriba siguen con el id entero.
                */}
                <td>{idVisible(fila.idUsuario)}</td>
                <td>{fila.nombre}</td>
                <td>{fila.telefono}</td>
                <td>{rotuloDeLote(fila.lote)}</td>
                <td>{rotuloDeBandera(fila.sinInteraccion)}</td>
                <td>{rotuloDeBandera(fila.siguioConChatbot)}</td>
                {deriva && <td>{rotuloDeBandera(fila.pidioHumano)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Paginado
        etiqueta="elegibles"
        pagina={pagina}
        total={total}
        tamanoDePagina={tamanoDePagina}
        unidad={{ singular: "elegible", plural: "elegibles" }}
        onPagina={setPagina}
        dentroDeUnFormulario
      >
        <label>
          Filas por página
          <select
            value={tamanoDePagina}
            onChange={(evento) => {
              setPagina(1)
              setTamanoDePagina(Number(evento.target.value))
            }}
          >
            {TAMANOS_DE_PAGINA.map((tamano) => (
              <option key={tamano} value={tamano}>
                {tamano}
              </option>
            ))}
          </select>
        </label>
      </Paginado>
    </fieldset>
  )
}

export type PropsFormularioEnvio = {
  readonly cargarElegibles: CargarElegibles
  /**
   * @deprecated `R2-UI2` — el selector ya no lee el CATÁLOGO sino la LISTA
   * «Plantillas para envíos» (`B13`). Se acepta para no romper a quien lo pasa
   * (`pantalla.tsx`), pero este componente no lo usa.
   */
  readonly cargarPlantillas?: CargarPlantillas
  /**
   * ⭐⭐ `R2-UI2` · **La fuente de «Plantillas para envíos» del brazo**, con el
   * brazo ya resuelto por quien monta la ruta. Va por PROP y no por un contexto
   * nuevo: `gates/pantalla-envios.spec.ts` DESCUBRE los contextos barriendo
   * `src/contexto-*.tsx`, y un contexto nuevo cambiaría lo que ese arnés monta.
   *
   * ⛔ Ausente ⇒ el selector ofrece CERO opciones y lo DICE. No se arma una lista
   * `ListaParaEnvios` vacía a mano: su `lastAttempt` no tiene forma sin inventar
   * un hecho del brazo.
   */
  readonly listaParaEnvios?: CargarListaParaEnvios
  /** `GET elegibles/lotes` del brazo, para el filtro por lote. */
  readonly lotesDe?: () => Promise<readonly string[]>
  /**
   * `R1.1-F4-CA-79` · el brazo de la dirección, para el aviso de por qué número
   * sale el envío. Ausente ⇒ el aviso no se dibuja.
   */
  readonly brazo?: Brazo
  /**
   * ⭐ `R2-UI2` / `CA-47` · recibe la fila CREADA (lo que devuelve `borde.crear`)
   * para que Envíos pueda decir «Envío «[nombre]» creado para N participantes.»
   * con el `audienceSize` que decidió el SERVIDOR y no con lo que la pantalla
   * contó de su lado —pueden discrepar, y el aviso tiene que decir la verdad del
   * envío que existe—.
   */
  readonly onCreado: (creado: { readonly name: string; readonly audienceSize: number }) => void
  /**
   * Si no viene, «Cancelar» navega atrás con `navigate(-1)`.
   *
   * ⛔ La navegación NO puede quedar en un `useState` local (`mostrandoFormulario`):
   * volver a la lista es una transición de la RUTA, y quien la resuelve es el
   * router. Un estado local deja la URL mintiendo.
   */
  readonly onCancelar?: () => void
}

export function FormularioEnvio(props: PropsFormularioEnvio): JSX.Element {
  const { cargarElegibles, onCreado } = props
  const borde = useBordeEnvios()
  const navigate = useNavigate()

  const [nombre, setNombre] = useState("")
  const [elegida, setElegida] = useState<FilaParaEnvios | null>(null)
  /**
   * ⭐ `R2-UI12` / `CA-15`/`CA-41` · una variable AUSENTE de este registro está
   * SIN ELEGIR. Nadie la completa por defecto (ver el rastro de abajo).
   */
  const [parametros, setParametros] = useState<Readonly<Record<string, TemplateParameterSource>>>(
    {},
  )
  /*
   * ⭐ SELECTOR DE FECHA Y HORA (29/10/2026). El campo dejó de ser UN texto y son
   * DOS controles nativos: `<input type="date">` y `<input type="time">`.
   *
   * ⛔ `datetime-local` sigue FUERA, y por la misma razón que A16 lo retiró: su
   * valor es el instante del reloj del NAVEGADOR. Acá los dos controles entregan
   * componentes sueltos —`aaaa-mm-dd` y `hh:mm`— que se pegan como texto
   * `dd-mm-aaaa hh:mm` SIN pasar por `Date`: la zona la sigue decidiendo el
   * servidor, que es lo que el contrato pide (`scheduledForLocal`).
   */
  const [programacionDia, setProgramacionDia] = useState("")
  const [programacionHora, setProgramacionHora] = useState("")
  const programacion = componerProgramacion(programacionDia, programacionHora)
  const [seleccion, setSeleccion] = useState<SeleccionDeAudiencia>({
    todos: false,
    ids: new Set(),
    excluidos: new Set(),
  })
  /*
   * ⛔⭐ **RASTRO · 24/9/2026, `R2-UI15`** (R2-H-045). Acá vivía
   * ~~`idsVistos`~~: los ids de las páginas que el navegador había cargado, que con
   * «Seleccionar los M» se mandaban como `recipients` (menos los destildados). Tres
   * defectos: con más de una página el envío salía a los VISTOS y no a M; la
   * primera línea contaba los vistos; y el conjunto no se vaciaba al aplicar otro
   * filtro (con «Lote H» salían 52 ids, «52 de 2»). La selección total ahora viaja
   * como CRITERIO (`audience`), así que la consola ya no junta ids y este estado
   * sobra entero.
   */
  const [filtros, setFiltros] = useState<FiltrosDeAudiencia>({ lotes: [], estados: [] })
  /** `R2-UI16` · el orden de las casillas de lote cuando se aplicó `filtros`. */
  const [ordenDeLotes, setOrdenDeLotes] = useState<readonly string[]>([])
  const [totalElegibles, setTotalElegibles] = useState(0)
  const [enCurso, setEnCurso] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [intentado, setIntentado] = useState(false)
  /** `CA-37` · se cambió el filtro con gente ya seleccionada. */
  const [avisoFiltro, setAvisoFiltro] = useState(false)

  /**
   * La lista del brazo, cruda; ~~el filtro por `sendability` se hace a la vista.~~
   *
   * ⛔⭐ **RASTRO · 24/9/2026, `R2-UI12`.** Ya no hay filtro por `sendability` en
   * ningún lado de esta pantalla: el selector ofrece la lista ENTERA
   * (`PRD_R2.md:1396`, «aprobadas o no»; `:1464`). Ver el rastro de
   * `plantillasOfrecidas`.
   */
  const [filasParaEnvios, setFilasParaEnvios] = useState<readonly FilaParaEnvios[]>([])
  const [errorLista, setErrorLista] = useState<string | null>(null)

  useEffect(() => {
    if (props.listaParaEnvios === undefined) return
    let cancelado = false

    void props
      .listaParaEnvios()
      .then((traida) => {
        if (cancelado) return
        setFilasParaEnvios(traida.filas)
      })
      .catch((causa: unknown) => {
        if (cancelado) return
        setErrorLista(causa instanceof Error ? causa.message : String(causa))
      })

    return () => {
      cancelado = true
    }
  }, [props.listaParaEnvios])

  /**
   * ~~⭐ **SÓLO LO QUE SE PUEDE ENVIAR.** El veredicto lo trae `sendability` DECIDIDO
   * desde el borde (`B13`/`RT 9`): recalcularlo acá desde `catalog.approvalStatus`
   * dejaría dos lugares decidiendo, y el día que diverjan la pantalla diría «sí» y
   * el envío fallaría con «La plantilla no se puede usar.».~~
   *
   * ⛔⭐ **RASTRO · 24/9/2026, `R2-UI12`** — se revierte, con su fuente:
   * `PRD_R2.md:1396` («Selector con las plantillas de «Plantillas para envíos» del
   * brazo (R2-F2), aprobadas o no, por nombre y sin estado.») y `PRD_R2.md:1464`
   * («Plantilla de la lista que no está aprobada | Se puede elegir; al salir el
   * envío queda «Fallido» (R2-F5)»). El argumento de arriba era correcto —no dos
   * lugares decidiendo— y por eso mismo el filtro sobraba: con él puesto, la
   * PANTALLA era el segundo lugar, y decidía lo contrario del PRD. Quien verifica
   * si la plantilla se puede usar es el motor al ejecutar; con el filtro,
   * `R2-F5-CA-47`/`CA-49` (crear un envío con una no aprobada y verlo «Fallido»)
   * eran imposibles desde acá. Tampoco se muestra el estado al lado del nombre:
   * «sin estado» es de la misma línea del PRD.
   */
  const plantillasOfrecidas = filasParaEnvios

  /**
   * Las variables de la plantilla elegida, EN ORDEN (el orden del arreglo es
   * dato). Si el catálogo no la tiene (`catalog === null`) no hay nombres de
   * variable: se dice, no se inventa una fila.
   */
  const variablesDeLaPlantilla = useMemo<readonly string[]>(
    () => elegida?.catalog?.variables ?? [],
    [elegida],
  )

  /*
   * ⛔⭐ **RASTRO · 24/9/2026, `R2-UI12`** (`CA-15`, `CA-41`). Acá había un efecto
   * que, al cambiar las variables, completaba cada una con
   * ~~`previos[variable] ?? "nombre"`~~: toda variable ARRANCABA en «Nombre del
   * participante». Eso hacía que un envío con la variable olvidada saliera con el
   * nombre en un lugar que quizás pedía el teléfono, sin que nadie lo eligiera, y
   * dejaba inalcanzable `MENSAJE_FALTA_VARIABLE`. Ahora cada variable arranca SIN
   * ELEGIR, y cambiar de plantilla rehace el bloque desde cero (`CA-15`): lo hace
   * el `onChange` del selector de plantilla, que es el único que la cambia.
   */


  /**
   * ⭐ `CA-39…45` · **TODOS los errores, a la vez.** `Regla 10`/`CA-45`: «Enviar»
   * nunca se deshabilita, así que lo que falta se dice al apretar, entero y sobre
   * cada campo. Cada mensaje es el literal que mide el gate.
   */
  const programada = parsearProgramacion(programacion)
  const programacionInvalida = programacion.trim() !== "" && programada === "invalida"
  const programacionPasada =
    programada !== null && programada !== "invalida" && programada.getTime() <= Date.now()

  /**
   * ⭐ `CP-F2A-003` §② · **cada error viaja con el RÓTULO de su campo además del
   * motivo.** La live region tiene que decir CUÁL falta (el rótulo) y POR QUÉ (el
   * motivo): un anuncio que dijera sólo «Selecciona al menos un participante.»
   * obliga al operador a adivinar de qué bloque habla. `aw10-envios.test.tsx`
   * afirma que el anuncio nombra «Selección de usuarios» cuando es esa la que
   * falta.
   *
   * ⭐ RASTRO · 24/9/2026 · `R2-UI13`. El rótulo pasó a «Audiencia» (`CA-05`) y la
   * prueba nombra ése. Y por errata `R2-D1` ESTA región es el único lugar que
   * antepone el rótulo: debajo de cada campo va sólo el texto del PRD.
   */
  const errores: { readonly rotulo: string; readonly texto: string }[] = []
  const nombreLimpio = nombre.trim()
  if (nombreLimpio === "") {
    errores.push({ rotulo: ROTULO_NOMBRE, texto: MENSAJE_FALTA_NOMBRE })
  } else if (nombreLimpio.length > LIMITE_DE_NOMBRE) {
    errores.push({ rotulo: ROTULO_NOMBRE, texto: MENSAJE_NOMBRE_LARGO })
  }
  if (elegida === null) {
    errores.push({ rotulo: ROTULO_PLANTILLA, texto: MENSAJE_FALTA_PLANTILLA })
  }
  /**
   * ⭐ `R2-UI12` / `CA-76` · el texto que va DEBAJO del nombre, el que
   * corresponda: vacío o más de 100. `null` = el nombre está bien. Antes sólo el
   * vacío se decía debajo del campo; el largo sólo en la lista de arriba.
   */
  /*
   * ⭐ RASTRO · 24/9/2026, noche · `R2-UI13` (errata `R2-D1` con su agregado,
   * decisión de Galo; `CA-39`, `PRD_R2.md:1436`). Con el nombre vacío decía
   * ~~`${ROTULO_NOMBRE} ${MARCA_OBLIGATORIO}: ${MENSAJE_FALTA_NOMBRE}`~~, que
   * venía de `CP-F2A-003` §② (R1.1: la descripción accesible decía «obligatorio»).
   * R2-D1 la supera: debajo del campo, y por lo tanto en su `aria-describedby`,
   * va SÓLO el texto del PRD. Lo obligatorio lo dicen `required` y la marca de la
   * `<label>`; el rótulo, la región de arriba.
   */
  const errorDeNombreDebajo: string | null =
    nombreLimpio === ""
      ? MENSAJE_FALTA_NOMBRE
      : nombreLimpio.length > LIMITE_DE_NOMBRE
        ? MENSAJE_NOMBRE_LARGO
        : null
  /** `R2-UI12` / `CA-41` · hay plantilla y alguna de sus variables sigue sin elegir. */
  const faltaVariable =
    elegida !== null && variablesDeLaPlantilla.some((variable) => parametros[variable] === undefined)
  if (faltaVariable) {
    errores.push({ rotulo: ROTULO_VARIABLES, texto: MENSAJE_FALTA_VARIABLE })
  }
  /**
   * ⭐⭐ `faltaAudiencia` se calcula UNA vez y sirve para DOS cosas: listar el
   * motivo en la live region y DESCRIBIR el `<fieldset>` (su `aria-describedby`).
   *
   * ⛔ `AW10` §`CP-F2A-003` §② — **la señal NO se condiciona al intento de
   * envío**: un campo obligatorio y vacío lo dice aunque nadie haya apretado
   * «Enviar». Con «Enviar» ya no deshabilitado (`Regla 10`), un error que esperara
   * al `submit` sería un error que el operador puede no ver nunca.
   */
  const faltaAudiencia = seleccion.todos
    ? totalElegibles - seleccion.excluidos.size <= 0
    : seleccion.ids.size === 0
  if (faltaAudiencia) {
    errores.push({ rotulo: ROTULO_AUDIENCIA, texto: MENSAJE_FALTA_AUDIENCIA })
  }
  if (programacionInvalida) {
    errores.push({ rotulo: ROTULO_PROGRAMACION, texto: MENSAJE_PROGRAMACION_INVALIDA })
  } else if (programacionPasada) {
    errores.push({ rotulo: ROTULO_PROGRAMACION, texto: MENSAJE_PROGRAMACION_PASADA })
  }

  const enviar = async () => {
    if (enCurso) return

    /*
     * `CP-F2A-003` · **un envío sin un solo destinatario no sale de acá.** «Enviar»
     * ya no está `disabled` (`Regla 10`/`CA-45`), así que esta guarda es la única
     * que impide el registro vacío; los motivos ya se ven en la lista de arriba.
     */
    const programadaAlEnviar = parsearProgramacion(programacion)
    if (programadaAlEnviar === "invalida") return
    if (programadaAlEnviar !== null && programadaAlEnviar.getTime() <= Date.now()) return
    if (nombreLimpio === "" || nombreLimpio.length > LIMITE_DE_NOMBRE || elegida === null) return

    /*
     * ⭐ `R2-UI15` · M − destildados con selección total; los marcados si no. Es la
     * misma cuenta que dibuja la barra, y ya no depende de las páginas cargadas.
     */
    const seleccionados = seleccion.todos
      ? Math.max(0, totalElegibles - seleccion.excluidos.size)
      : seleccion.ids.size

    if (seleccionados === 0) return

    /*
     * ⛔⭐ **RASTRO · 24/9/2026, `R2-UI12`** (`CA-41`). Acá decía
     * ~~`parametros[variable] ?? "nombre"`~~: una variable sin elegir viajaba como
     * «Nombre del participante». Ahora una sola sin elegir corta el envío (sin
     * `POST`); el motivo ya está debajo del bloque Variables y en la lista.
     */
    const parameters: TemplateParameterSource[] = []
    for (const variable of variablesDeLaPlantilla) {
      const fuente = parametros[variable]
      if (fuente === undefined) return
      parameters.push(fuente)
    }
    const template: TemplateContent = {
      kind: "plantilla",
      name: elegida.name,
      language: elegida.language,
      parameters,
    }

    setEnCurso(true)
    setError(null)

    try {
      const comun = {
        name: nombreLimpio,
        template,
        // ⭐ `ADR-0176` §ENMIENDA — la MISMA función que dibuja el rótulo.
        audienceLabel: etiquetaDeAudiencia(seleccionados, totalElegibles),
        /*
         * ⭐ `R2-UI16` · la segunda línea, por las DOS vías (criterio y lista), del
         * filtro APLICADO. Sin filtro, `null` (nunca `""`).
         */
        audienceFilterLabel: segundaLineaDeAudiencia(filtros, ordenDeLotes),
        scheduledFor: programadaAlEnviar,
      }
      /*
       * ⛔⭐ **RASTRO · 24/9/2026, `R2-UI15`** (R2-H-045). Con selección total esto
       * mandaba ~~`recipients` (los ids VISTOS menos los destildados) y además un
       * `exclude` suelto en la raíz~~, clave que el contrato no declara: `400`, y el
       * envío no se creaba (CA-49). Ahora es O CRITERIO O LISTA
       * (`DispatchAudience`, `ADR-0328`): con «Seleccionar los M» viaja
       * `audience: {lotes, estados, exclude}` con el filtro APLICADO (no el
       * borrador) y «ninguno» como `null`, nunca `[]`; a mano, `recipients`.
       */
      const command: ComandoDeEnvio = seleccion.todos
        ? {
            ...comun,
            audience: {
              lotes: tuplaONull(filtros.lotes),
              estados: tuplaONull(filtros.estados),
              exclude: Array.from(seleccion.excluidos),
            },
          }
        : { ...comun, recipients: Array.from(seleccion.ids) }

      const creado = await borde.crear(command)
      onCreado({ name: creado.name, audienceSize: creado.audienceSize })
    } catch (causa) {
      setError(causa instanceof Error ? causa.message : String(causa))
    } finally {
      setEnCurso(false)
    }
  }

  return (
    <>
      <h1>Nuevo envío</h1>

      {props.brazo !== undefined && (
        <p className="nota" role="note">
          {avisoDelEnvio(props.brazo)}
        </p>
      )}

      {/*
        ⛔ `noValidate` NO es decorativo: `#envio-nombre` y `#envio-plantilla`
        llevan `required`, y sin esto el navegador corta el submit con sus
        burbujas nativas ANTES de correr el handler — la lista de errores de
        abajo (`CA-45`, todos juntos) nunca se dibujaría. `required` queda como
        pista de accesibilidad; la validación la hace `enviar()`.
      */}
      <form
        noValidate
        onSubmit={(evento) => {
          evento.preventDefault()
          /*
           * ⛔⭐ `R2-UI12` · decisión propia, declarada en el commit. Con más de una
           * página de elegibles, `<Paginado>` dibujaba su propio `<form>` («Ir a la
           * página») ADENTRO de éste, y el `submit` de React burbujea por el árbol:
           * medido con el doble, apretar «Ir» con el formulario completo CREABA el
           * envío. Sólo el `submit` de ESTE formulario envía.
           *
           * ⭐ RASTRO · 3/11/2026 · desde ahora el paginado de elegibles va con
           * `dentroDeUnFormulario` y ya no hay `<form>` anidado (HTML inválido, que
           * React avisaba y el humo de la consola contaba como pantalla rota). Medido
           * en Chromium contra el ambiente local, con el anidado «Ir» —clic o Enter—
           * hacía el envío NATIVO del `<form>` interno: recargaba la consola (`/?#/…`),
           * borraba nombre, plantilla y selección, y no pasaba de página. La guarda se
           * queda: es barata y cubre cualquier `<form>` que vuelva a entrar.
           */
          if (evento.target !== evento.currentTarget) return
          setIntentado(true)
          void enviar()
        }}
      >
        {/*
          ⭐⭐ `CP-F2A-003` §② / `CA-45` · **la región que anuncia EXISTE SIEMPRE y
          arranca VACÍA**, y al dispararse lista TODOS los errores juntos (uno por
          uno obligaría a apretar «Enviar» tantas veces como campos falten).

          `alertasDelFormulario` de `AW10` exige exactamente UN `[role=alert]`
          dentro del `<form>` con texto `""` al montar: una live region tiene que
          estar en el árbol de accesibilidad ANTES de que su contenido cambie, o la
          tecnología asistiva no tiene mutación que observar. Montarla recién
          cuando hay error la vuelve muda la primera vez.
          ⛔ Y cada renglón va con el RÓTULO de su campo: el anuncio dice CUÁL falta.
        */}
        <ul role="alert">
          {intentado &&
            errores.map(({ rotulo, texto }) => (
              <li key={texto}>
                {rotulo}: {texto}
              </li>
            ))}
        </ul>

        <div>
          <label htmlFor="envio-nombre">
            {ROTULO_NOMBRE} <span aria-hidden="true">{MARCA_OBLIGATORIO}</span>
          </label>
          <input
            id="envio-nombre"
            type="text"
            required
            value={nombre}
            onChange={(evento) => setNombre(evento.target.value)}
            disabled={enCurso}
            aria-invalid={errorDeNombreDebajo !== null ? true : undefined}
            aria-describedby={errorDeNombreDebajo !== null ? ID_ERROR_NOMBRE : undefined}
          />
          {/*
            ⛔⭐ **RASTRO · 24/9/2026, `R2-UI12`** (`CA-76`). Este `<p>`, su
            `aria-invalid` y su `aria-describedby` se encendían ~~sólo con
            `nombre.trim() === ""`~~: con 101 caracteres el campo quedaba «válido»
            para el lector de pantalla y el tope sólo se decía en la lista de
            arriba. Ahora dicen el error que corresponda, vacío o largo. La lista
            (`CA-45`, todos juntos) sigue igual.
          */}
          {errorDeNombreDebajo !== null && <p id={ID_ERROR_NOMBRE}>{errorDeNombreDebajo}</p>}
        </div>

        <div>
          <label htmlFor="envio-plantilla">
            {ROTULO_PLANTILLA} <span aria-hidden="true">{MARCA_OBLIGATORIO}</span>
          </label>
          {/**
           * ⛔ **Lo que este `<select>` NO promete**: ofrece lo que la LISTA del
           * brazo trae, no lo que Meta tiene ahora. La re-validación al ejecutar es
           * de `canSend` y del motor de envío, no de esta pantalla.
           */}
          <select
            id="envio-plantilla"
            required
            value={elegida === null ? "" : `${elegida.name}·${elegida.language}`}
            onChange={(evento) => {
              const clave = evento.target.value
              setElegida(filasParaEnvios.find((f) => `${f.name}·${f.language}` === clave) ?? null)
              // ⭐ `R2-UI12` / `CA-15` · cambiar de plantilla rehace Variables: todas sin elegir.
              setParametros({})
            }}
            disabled={enCurso}
            aria-invalid={elegida === null ? true : undefined}
            aria-describedby={elegida === null ? ID_ERROR_PLANTILLA : undefined}
          >
            <option value="">{OPCION_INICIAL_DE_LA_LISTA}</option>
            {plantillasOfrecidas.map((fila) => (
              /*
               * El VALOR lleva `name·language` porque ésa es la terna que
               * identifica la fila; el envío guarda los dos campos por separado
               * (`TemplateContent.name` + `.language`), así que acá se separan.
               */
              <option key={`${fila.name}·${fila.language}`} value={`${fila.name}·${fila.language}`}>
                {fila.name} · {fila.language}
              </option>
            ))}
          </select>
          {/* ⭐ `R2-UI12` / `CA-07` · de dónde salen las plantillas, bajo el selector. */}
          <p className="nota">{AYUDA_PLANTILLA}</p>
          {elegida === null && <p id={ID_ERROR_PLANTILLA}>{MENSAJE_FALTA_PLANTILLA}</p>}
          {errorLista !== null && (
            <p className="envios__error" role="alert">
              No se pudo leer la lista de plantillas para envíos del brazo. {errorLista}
            </p>
          )}
          {props.listaParaEnvios === undefined && (
            <p className="envios__error" role="alert">
              No hay lista de plantillas para envíos de este brazo.
            </p>
          )}
        </div>

        <div id="envio-variables">
          <p className="envios__rotulo-maqueta">{ROTULO_VARIABLES}</p>
          {/* ⭐ `R2-UI12` / `CA-16` · con qué se completa cada variable. */}
          <p className="nota">{AYUDA_VARIABLES}</p>
          {variablesDeLaPlantilla.length === 0 && (
            <p className="maqueta-sin-datos">
              {/*
                ⭐ `CA-13` · sin plantilla el bloque NO inventa una frase: muestra
                «—», el estado vacío que `R1.1-F4-CA-84` mide literal. `CA-12` es
                el caso DISTINTO —plantilla elegida que no declara variables— y
                ése sí dice «Sin variables».
              */}
              {elegida === null ? "—" : "Sin variables."}
            </p>
          )}
          {variablesDeLaPlantilla.map((variable) => (
            <label key={variable}>
              {/*
                ⭐ `CA-10` · cada fila se rotula con la variable REAL de la
                plantilla (`{{1}}`, `{{nombre}}`), en el ORDEN que trae el
                catálogo: el orden del arreglo es dato.
              */}
              {`{{${variable}}}`}
              {/*
                ⛔⭐ **RASTRO · 24/9/2026, `R2-UI12`** (`CA-15`/`CA-41`). El valor
                era ~~`parametros[variable] ?? "nombre"`~~ y no había opción
                vacía: «sin elegir» no existía. Ahora arranca en `""` y la opción
                vacía lo dice; los DATOS siguen siendo los dos de `CA-14`.
              */}
              <select
                value={parametros[variable] ?? ""}
                onChange={(evento) => {
                  const valor = evento.target.value
                  setParametros((previos) => {
                    const siguientes: Record<string, TemplateParameterSource> = { ...previos }
                    if (valor === "nombre" || valor === "telefono") siguientes[variable] = valor
                    else delete siguientes[variable]
                    return siguientes
                  })
                }}
                disabled={enCurso}
                aria-invalid={parametros[variable] === undefined ? true : undefined}
                aria-describedby={
                  parametros[variable] === undefined ? ID_ERROR_VARIABLES : undefined
                }
              >
                <option value="">{OPCION_VARIABLE_SIN_ELEGIR}</option>
                {/* `CA-14` · los DOS datos del participante, vocabulario `ADR-0058`. */}
                <option value="nombre">Nombre del participante</option>
                <option value="telefono">Teléfono del participante</option>
              </select>
            </label>
          ))}
          {/* ⭐ `R2-UI12` / `CA-41` · el error va DEBAJO del bloque Variables. */}
          {faltaVariable && <p id={ID_ERROR_VARIABLES}>{MENSAJE_FALTA_VARIABLE}</p>}
        </div>

        {/*
          ⛔ `exactOptionalPropertyTypes` (tsconfig de la app) distingue «la prop
          ausente» de «la prop con `undefined`»: `lotesDe={props.lotesDe}` no
          compila cuando el llamador no pasó la prop. Es el mismo spread
          condicional que usa `envios/pantalla.tsx:532`.
        */}
        <SeleccionDeAudiencia
          cargarElegibles={cargarElegibles}
          {...(props.lotesDe === undefined ? {} : { lotesDe: props.lotesDe })}
          seleccion={seleccion}
          onSeleccionChange={setSeleccion}
          total={totalElegibles}
          onTotalChange={setTotalElegibles}
          onFiltrosChange={(nuevos, orden) => {
            /*
             * `CA-37` / `PRD_R2.md:1428` · con al menos un seleccionado, aplicar un
             * filtro DISTINTO («Aplicar filtro» o «Limpiar») LIMPIA la selección y
             * avisa. Re-aplicar el mismo filtro no cambia el conjunto: no limpia.
             */
            const clave = (f: FiltrosDeAudiencia) =>
              JSON.stringify([[...f.lotes].sort(), [...f.estados].sort()])
            const distinto = clave(nuevos) !== clave(filtros)
            setFiltros(nuevos)
            setOrdenDeLotes(orden)
            const haySeleccion = seleccion.todos
              ? totalElegibles - seleccion.excluidos.size > 0
              : seleccion.ids.size > 0
            if (distinto && haySeleccion) {
              setSeleccion({ todos: false, ids: new Set(), excluidos: new Set() })
              setAvisoFiltro(true)
            }
          }}
          falta={faltaAudiencia}
          {...(props.brazo === undefined ? {} : { brazo: props.brazo })}
        />

        {/*
          ⛔⭐ **RASTRO · 24/9/2026, `R2-UI16`** (errata `R2-D3`, decisión de Galo).
          Acá se dibujaba una nota ~~«Lotes: {…|todos} · Estados: {…|todos}»~~ con
          el filtro aplicado. No era del PRD (`:1456` pide la parte ausente, no
          «todos») y se QUITA: la segunda línea sólo viaja en el cuerpo del `POST`
          (`segundaLineaDeAudiencia`) y se ve en la lista de Envíos.
        */}
        {avisoFiltro && (
          <p className="nota" role="status">
            Cambiaste el filtro: vuelve a seleccionar
          </p>
        )}

        <div className="envios__programacion">
          <label htmlFor="envio-programacion">{ROTULO_PROGRAMACION}</label>
          <input
            id="envio-programacion"
            type="date"
            value={programacionDia}
            onChange={(evento) => setProgramacionDia(evento.target.value)}
            disabled={enCurso}
            /* El formato del cable, a mano: el control muestra `dd/mm/aaaa`. */
            title={FORMATO_PROGRAMACION}
            aria-label={`${ROTULO_PROGRAMACION} · fecha`}
            aria-invalid={programacionInvalida || programacionPasada ? true : undefined}
            aria-describedby={
              programacionInvalida || programacionPasada ? ID_ERROR_PROGRAMACION : undefined
            }
          />
          <input
            id="envio-programacion-hora"
            type="time"
            value={programacionHora}
            onChange={(evento) => setProgramacionHora(evento.target.value)}
            disabled={enCurso}
            aria-label={`${ROTULO_PROGRAMACION} · hora`}
            aria-invalid={programacionInvalida || programacionPasada ? true : undefined}
            aria-describedby={
              programacionInvalida || programacionPasada ? ID_ERROR_PROGRAMACION : undefined
            }
          />
          {/*
            ⛔⭐ **RASTRO · 24/9/2026, `R2-UI12`** (`CA-17`, `PRD_R2.md:1562`). Decía
            ~~«Formato dd-mm-aaaa hh:mm, hora Chile. Vacía = se ejecuta en el
            momento.»~~: una paráfrasis, y el caso compara el literal.

            ⭐ RASTRO · 29/10/2026 · **SELECTOR DE FECHA Y HORA.** El campo dejó de
            ser un texto y son dos controles nativos (`date` + `time`); el formato
            `dd-mm-aaaa hh:mm` ya no lo muestra un `placeholder` sino el `title`
            del control de fecha, y el valor sigue siendo el mismo texto que
            `scheduledForLocal` espera. La ayuda del PRD no cambió: sigue acá,
            debajo del campo, y sigue diciendo que vacía = al momento.
          */}
          <p>{AYUDA_PROGRAMACION}</p>
          {programacionInvalida && <p id={ID_ERROR_PROGRAMACION}>{MENSAJE_PROGRAMACION_INVALIDA}</p>}
          {programacionPasada && <p id={ID_ERROR_PROGRAMACION}>{MENSAJE_PROGRAMACION_PASADA}</p>}
        </div>

        {error !== null && <p role="alert">{error}</p>}

        {/*
          ⛔ `Regla 10`/`CA-45` · **«Enviar» NO se deshabilita por campos
          faltantes.** Un botón gris no dice CUÁL falta; el error se dice al
          apretar y sobre cada campo. La doble presentación la impide `enCurso`.
        */}
        <button type="submit">{enCurso ? "Enviando…" : "Enviar"}</button>
        <button
          type="button"
          onClick={() => {
            if (props.onCancelar !== undefined) props.onCancelar()
            else navigate(-1)
          }}
          disabled={enCurso}
        >
          Cancelar
        </button>
      </form>
    </>
  )
}
